import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  TextField,
  Chip,
  Checkbox,
  LinearProgress,
  InputAdornment,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Sports as SportsIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  SelectAll as SelectAllIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { importService, ScheduleGroup, DiscoveryResponse, DiscoveryProgress } from '@/services/importService';

interface GotSportDiscoveryDialogProps {
  open: boolean;
  onClose: () => void;
  onBack?: () => void; // For wizard mode - go back to previous step
  initialUrl?: string;
  autoStart?: boolean; // Automatically start discovery when opened
  onGroupsSelected: (groups: ScheduleGroup[], eventName: string) => void;
}

const GotSportDiscoveryDialog: React.FC<GotSportDiscoveryDialogProps> = ({
  open,
  onClose,
  onBack,
  initialUrl = '',
  autoStart = false,
  onGroupsSelected
}) => {
  // URL phase state
  const [url, setUrl] = useState(initialUrl);
  const [discovering, setDiscovering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Progress state
  const [progress, setProgress] = useState<DiscoveryProgress | null>(null);

  // Groups phase state
  const [discoveryResult, setDiscoveryResult] = useState<DiscoveryResponse | null>(null);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Filters
  const [ageFilter, setAgeFilter] = useState<string | null>(null);
  const [genderFilter, setGenderFilter] = useState<string | null>(null);
  const [divisionFilter, setDivisionFilter] = useState<string | null>(null);

  // Teams preview state
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [groupTeams, setGroupTeams] = useState<Record<string, string[]>>({});
  const [loadingTeams, setLoadingTeams] = useState<string | null>(null);
  const [teamsError, setTeamsError] = useState<string | null>(null);

  // Track if we've auto-started
  const [hasAutoStarted, setHasAutoStarted] = useState(false);

  // Ref to track SSE connection for cleanup
  const sseRef = useRef<{ close: () => void } | null>(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setUrl(initialUrl);
      setDiscovering(false);
      setError(null);
      setProgress(null);
      setDiscoveryResult(null);
      setSelectedGroupIds(new Set());
      setSearchQuery('');
      setAgeFilter(null);
      setGenderFilter(null);
      setDivisionFilter(null);
      setHasAutoStarted(false);
      // Reset teams preview state
      setExpandedGroupId(null);
      setGroupTeams({});
      setLoadingTeams(null);
      setTeamsError(null);
    } else {
      // Cleanup SSE connection when dialog closes
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    }
  }, [open, initialUrl]);

  // Auto-start discovery when dialog opens with autoStart=true
  useEffect(() => {
    if (open && autoStart && initialUrl && !hasAutoStarted && !discoveryResult) {
      setHasAutoStarted(true);
      handleDiscover();
    }
  }, [open, autoStart, initialUrl, hasAutoStarted, discoveryResult]);

  // Extract unique filter values from groups
  const filterOptions = useMemo(() => {
    if (!discoveryResult?.groups) return { ageGroups: [], genders: [], divisions: [] };

    const ageGroups = [...new Set(discoveryResult.groups.map(g => g.ageGroup).filter(Boolean))] as string[];
    const genders = [...new Set(discoveryResult.groups.map(g => g.gender).filter(Boolean))] as string[];
    const divisions = [...new Set(discoveryResult.groups.map(g => g.division).filter(Boolean))] as string[];

    // Sort age groups numerically
    ageGroups.sort((a, b) => {
      const numA = parseInt(a.replace(/\D/g, ''), 10) || 0;
      const numB = parseInt(b.replace(/\D/g, ''), 10) || 0;
      return numA - numB;
    });

    // Sort divisions alphabetically
    divisions.sort((a, b) => a.localeCompare(b));

    return { ageGroups, genders, divisions };
  }, [discoveryResult?.groups]);

  // Filter and search groups
  const filteredGroups = useMemo(() => {
    if (!discoveryResult?.groups) return [];

    return discoveryResult.groups.filter(group => {
      if (ageFilter && group.ageGroup !== ageFilter) return false;
      if (genderFilter && group.gender !== genderFilter) return false;
      if (divisionFilter && group.division !== divisionFilter) return false;

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          group.name.toLowerCase().includes(query) ||
          group.ageGroup?.toLowerCase().includes(query) ||
          group.gender?.toLowerCase().includes(query) ||
          group.division?.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [discoveryResult?.groups, ageFilter, genderFilter, divisionFilter, searchQuery]);

  // Handle URL discovery with SSE progress streaming
  const handleDiscover = async () => {
    const urlToUse = url || initialUrl;
    if (!urlToUse.trim()) {
      setError('Please enter a URL');
      return;
    }

    setDiscovering(true);
    setError(null);
    setProgress(null);

    // Close any existing SSE connection
    if (sseRef.current) {
      sseRef.current.close();
    }

    // Start SSE-based discovery with progress tracking
    const sse = await importService.discoverEventGroupsWithProgress(urlToUse, {
      onProgress: (progressUpdate) => {
        setProgress(progressUpdate);
      },
      onResult: (result) => {
        if (!result.success) {
          setError(result.error || 'Failed to discover schedule groups');
          setDiscovering(false);
          return;
        }

        if (result.groups.length === 0) {
          setError('No schedule groups found at this URL. Make sure you entered a GotSport event page URL.');
          setDiscovering(false);
          return;
        }

        setDiscoveryResult(result);
        setDiscovering(false);
      },
      onError: (errorMessage) => {
        setError(errorMessage);
        setDiscovering(false);
      }
    });

    sseRef.current = sse;
  };

  // Handle group selection toggle
  const handleGroupToggle = (groupId: string) => {
    setSelectedGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Handle select all visible groups
  const handleSelectAll = () => {
    setSelectedGroupIds(new Set(filteredGroups.map(g => g.id)));
  };

  // Handle clear selection
  const handleClearSelection = () => {
    setSelectedGroupIds(new Set());
  };

  // Handle add selected groups
  const handleAddSelected = () => {
    if (!discoveryResult) return;

    const selectedGroups = discoveryResult.groups.filter(g => selectedGroupIds.has(g.id));
    onGroupsSelected(selectedGroups, discoveryResult.eventName);
  };

  // Handle back button
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      onClose();
    }
  };

  // Handle viewing teams for a group
  const handleViewTeams = async (group: ScheduleGroup, event: React.MouseEvent) => {
    event.stopPropagation(); // Don't toggle checkbox when clicking view teams

    // If already expanded for this group, collapse it
    if (expandedGroupId === group.id) {
      setExpandedGroupId(null);
      return;
    }

    // Expand this group
    setExpandedGroupId(group.id);
    setTeamsError(null);

    // If we already have teams for this group, don't fetch again
    if (groupTeams[group.id]) {
      return;
    }

    // Fetch teams
    setLoadingTeams(group.id);
    try {
      const result = await importService.fetchGroupTeams(group.scheduleUrl);
      if (result.success) {
        setGroupTeams(prev => ({ ...prev, [group.id]: result.teams }));
      } else {
        setTeamsError(result.error || 'Failed to load teams');
      }
    } catch (err) {
      setTeamsError('Failed to load teams');
    } finally {
      setLoadingTeams(null);
    }
  };

  // Clear filter
  const clearFilters = () => {
    setAgeFilter(null);
    setGenderFilter(null);
    setDivisionFilter(null);
    setSearchQuery('');
  };

  // Determine if we're in "discovering" or "selecting" mode
  const isDiscoveringPhase = !discoveryResult;
  const isSelectingPhase = !!discoveryResult;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableAutoFocus
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SportsIcon />
          <Typography variant="h6">
            {isSelectingPhase ? 'Select Schedule Groups' : 'Discovering Schedules'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isDiscoveringPhase ? (
          // Discovery Phase
          <Box>
            {discovering ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                <Box sx={{ width: '100%', maxWidth: 400, mb: 3 }}>
                  <LinearProgress
                    variant={progress?.percent !== undefined ? 'determinate' : 'indeterminate'}
                    value={progress?.percent || 0}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  {progress?.percent !== undefined && (
                    <Typography variant="body2" color="text.secondary" align="right" sx={{ mt: 0.5 }}>
                      {Math.round(progress.percent)}%
                    </Typography>
                  )}
                </Box>
                <Typography variant="h6" color="text.secondary">
                  {progress?.message || 'Discovering available schedule groups...'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {progress?.detail || 'This may take a moment'}
                </Typography>
                {progress?.groupsFound !== undefined && progress.groupsFound > 0 && (
                  <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                    Found {progress.groupsFound} groups{progress.groupsProcessed !== undefined ? `, processing ${progress.groupsProcessed}/${progress.groupsFound}` : ''}
                  </Typography>
                )}
              </Box>
            ) : error ? (
              <Box>
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
                <TextField
                  fullWidth
                  label="GotSport Event URL"
                  placeholder="https://system.gotsport.com/org_event/events/51028"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  helperText="Check the URL and try again"
                  sx={{ mb: 2 }}
                />
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                <Typography variant="body1" color="text.secondary">
                  Ready to discover schedules
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          // Group Selection Phase
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Found {discoveryResult.groups.length} schedule groups in "{discoveryResult.eventName}"
            </Alert>

            {/* Search and Filters */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search groups..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <Button size="small" onClick={() => setSearchQuery('')}>
                        <ClearIcon fontSize="small" />
                      </Button>
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
              />

              {/* Filter chips */}
              <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                <FilterIcon fontSize="small" color="action" />

                {filterOptions.ageGroups.map(age => (
                  <Chip
                    key={age}
                    label={age}
                    size="small"
                    variant={ageFilter === age ? 'filled' : 'outlined'}
                    color={ageFilter === age ? 'primary' : 'default'}
                    onClick={() => setAgeFilter(ageFilter === age ? null : age)}
                  />
                ))}

                {filterOptions.ageGroups.length > 0 && filterOptions.genders.length > 0 && (
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                )}

                {filterOptions.genders.map(gender => (
                  <Chip
                    key={gender}
                    label={gender}
                    size="small"
                    variant={genderFilter === gender ? 'filled' : 'outlined'}
                    color={genderFilter === gender ? 'secondary' : 'default'}
                    onClick={() => setGenderFilter(genderFilter === gender ? null : gender)}
                  />
                ))}

                {(filterOptions.genders.length > 0 || filterOptions.ageGroups.length > 0) && filterOptions.divisions.length > 0 && (
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                )}

                {filterOptions.divisions.map(division => (
                  <Chip
                    key={division}
                    label={division}
                    size="small"
                    variant={divisionFilter === division ? 'filled' : 'outlined'}
                    color={divisionFilter === division ? 'info' : 'default'}
                    onClick={() => setDivisionFilter(divisionFilter === division ? null : division)}
                  />
                ))}

                {(ageFilter || genderFilter || divisionFilter || searchQuery) && (
                  <Button size="small" onClick={clearFilters} sx={{ ml: 1 }} startIcon={<ClearIcon />}>
                    Clear filters
                  </Button>
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Selection controls */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {filteredGroups.length} groups shown, {selectedGroupIds.size} selected
              </Typography>
              <Box>
                <Button size="small" onClick={handleSelectAll} disabled={filteredGroups.length === 0} startIcon={<SelectAllIcon />}>
                  Select All
                </Button>
                <Button size="small" onClick={handleClearSelection} disabled={selectedGroupIds.size === 0} startIcon={<ClearIcon />}>
                  Clear
                </Button>
              </Box>
            </Box>

            {/* Groups list */}
            <List sx={{ maxHeight: '350px', overflow: 'auto', bgcolor: 'grey.50', borderRadius: 1 }}>
              {filteredGroups.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary="No groups match your filters"
                    secondary="Try adjusting your search or filters"
                  />
                </ListItem>
              ) : (
                filteredGroups.map(group => (
                  <Box key={group.id}>
                    <ListItem
                      disablePadding
                      secondaryAction={
                        <Button
                          size="small"
                          onClick={(e) => handleViewTeams(group, e)}
                          endIcon={
                            expandedGroupId === group.id ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )
                          }
                          sx={{
                            mr: 0.5,
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            minWidth: 'auto'
                          }}
                        >
                          {expandedGroupId === group.id ? 'Hide teams' : 'Show teams'}
                        </Button>
                      }
                    >
                      <ListItemButton onClick={() => handleGroupToggle(group.id)} dense>
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={selectedGroupIds.has(group.id)}
                            tabIndex={-1}
                            disableRipple
                            icon={<CheckBoxOutlineBlankIcon />}
                            checkedIcon={<CheckBoxIcon />}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={group.name}
                          secondary={
                            <Box display="flex" gap={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                              {group.ageGroup && (
                                <Chip label={group.ageGroup} size="small" variant="outlined" />
                              )}
                              {group.gender && (
                                <Chip label={group.gender} size="small" variant="outlined" />
                              )}
                              {group.division && (
                                <Chip label={group.division} size="small" variant="outlined" />
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>

                    {/* Collapsible teams section */}
                    <Collapse in={expandedGroupId === group.id} timeout="auto" unmountOnExit>
                      <Box sx={{ pl: 7, pr: 2, pb: 1.5, pt: 0.5, bgcolor: 'grey.100' }}>
                        {loadingTeams === group.id ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="text.secondary">
                              Loading teams...
                            </Typography>
                          </Box>
                        ) : teamsError && expandedGroupId === group.id ? (
                          <Typography variant="body2" color="error">
                            {teamsError}
                          </Typography>
                        ) : groupTeams[group.id]?.length > 0 ? (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                              {groupTeams[group.id].length} teams in this group:
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {groupTeams[group.id].map((team, idx) => (
                                <Chip
                                  key={idx}
                                  label={team}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.75rem' }}
                                />
                              ))}
                            </Box>
                          </Box>
                        ) : groupTeams[group.id]?.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No teams found in this group
                          </Typography>
                        ) : null}
                      </Box>
                    </Collapse>
                  </Box>
                ))
              )}
            </List>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleBack}
          disabled={discovering}
          startIcon={onBack ? <ArrowBackIcon /> : <CancelIcon />}
        >
          {onBack ? 'Back' : 'Cancel'}
        </Button>

        {isDiscoveringPhase && error && (
          <Button
            variant="contained"
            onClick={handleDiscover}
            disabled={discovering || !url.trim()}
            startIcon={<RefreshIcon />}
          >
            Retry
          </Button>
        )}

        {isSelectingPhase && (
          <Button
            variant="contained"
            onClick={handleAddSelected}
            disabled={selectedGroupIds.size === 0}
            startIcon={<AddIcon />}
          >
            Add ({selectedGroupIds.size})
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GotSportDiscoveryDialog;
